<app-spinner [isLoading]="isLoading$ | async">
  @if (!(isLoading$ | async)) {
  @if (pageDoesNotExist$ | async) {
  <app-not-existing-page></app-not-existing-page>
  } @else {
  @if ({
    customer: customer$ | async,
    isSignedIn: isSignedIn$ | async,
    customerAnnouncementList: customerAnnouncementList$ | async,
    isJobseekerSignedIn: isJobseekerSignedIn$ | async,
    isCustomerSignedIn: isCustomerSignedIn$ | async,
    isCustomerLoaded: isCustomerLoaded$ | async,
    providerWebsiteUrlMap: providerWebsiteUrlMap$ | async,
    userType: userType$ | async
  }; as data) {

  <!--
    .page has to stay the first node of this view: a @let in front of it makes hydration
    find it by a DOM path through app-spinner, which breaks once the spinner overlay is
    inserted while the customer loads ("hasAttribute is not a function")
  -->
  <div class="page" [class.page--branded]="!!brandColorOf(data.customer)" [style.--brand]="brandColorOf(data.customer)">
    @let customer = data.customer;
    @let announcements = data.customerAnnouncementList ?? [];
    <!-- the cover photo opens the about card; without a cover the card starts straight at its content -->
    @let coverUrl = customer?.isWithCover ? customer.coverImageUrl : null;
    @let hasIndustries = customer?.showIndustry && customer.industryList?.length > 0;
    @let hasWebsite = customer?.showWebsite && !!customer.website;
    @let hasDescription = customer?.showDescription && !!customer.description;
    @let canApply = data.userType !== userType.Internal;

    @if (data.isCustomerLoaded) {
    <main class="page__inner">

      <section
        class="card about"
        [class.about--branded]="customer.isWithBranding"
        [class.about--with-cover]="!!coverUrl"
      >
        @if (coverUrl) {
        <div class="about__cover">
          <img class="about__cover-img" [src]="coverUrl" [alt]="customer.name" />
        </div>
        }

        <div class="about__body">
          <div class="about__row">
            <div class="about__logo">
              @if (customer.logoHref) {
              <img class="about__logo-img" [src]="customer.logoHref" [alt]="customer.name" />
              } @else {
              <span class="about__logo-initial">{{ logoInitial(customer.name) }}</span>
              }
            </div>

            <div class="about__title">
              <h1 class="about__name">{{ customer.name }}</h1>

              @if (hasIndustries) {
              <div class="about__fact">
                <img class="about__fact-icon" src="assets/img/customer/industry-icon.svg" width="15" height="15" alt="" />
                <span class="about__fact-label">{{ 'label.IndustryField' | translate }}:</span>
                <span class="about__fact-value">{{ industriesText(customer.industryList) }}</span>
              </div>
              }

              @if (hasWebsite) {
              <div class="about__fact">
                <img class="about__fact-icon" src="assets/img/customer/website-icon.svg" width="15" height="15" alt="" />
                <span class="about__fact-label">{{ 'label.Website' | translate }}:</span>
                <a class="about__fact-value about__fact-value--link" [href]="'//' + customer.website" target="_blank" rel="noopener noreferrer">
                  {{ customer.website }}
                </a>
              </div>
              }
            </div>
          </div>

          @if (hasDescription) {
          <div class="about__content">
            <h2 class="about__heading">{{ 'label.AboutCompany' | translate }}</h2>
            <div class="about__description" [innerHTML]="customer.description | safeHtml"></div>
          </div>
          }
        </div>
      </section>

      @if (customer.hasActiveCvBox) {
      <section id="cvbox" class="card cv-box">
        <h2 class="cv-box__title">{{ 'label.CvBox' | translate }}</h2>

        <div class="cv-box__body">
          <div class="cv-box__action">
            <!-- the box with the company's logo in its slot is an illustration; the real action is the button under it -->
            <div class="cv-box__art" aria-hidden="true">
              <img class="cv-box__art-img" src="assets/img/cv-box/cv-box-redesign.png" width="140" height="129" alt="" />
              <div class="cv-box__art-logo">
                @if (customer.logoThumbnailHref || customer.logoHref) {
                <img class="cv-box__art-logo-img" [src]="customer.logoThumbnailHref || customer.logoHref" alt="" />
                } @else {
                <span class="cv-box__art-logo-initial">{{ logoInitial(customer.name) }}</span>
                }
              </div>
              <span class="cv-box__art-btn">{{ 'label.LeaveCv' | translate }}</span>
            </div>

            @if (canApply) {
            <span class="cv-box__offer">{{ 'label.LeaveCvOffer' | translate }}</span>

            @if (customer.isBeenApplied) {
            @if (data.isJobseekerSignedIn) {
            <button type="button" class="cv-box__btn" disabled>{{ 'label.CvAlreadyLeft' | translate }}</button>
            }
            } @else {
            <!-- a company account cannot leave a CV; the note at the bottom of the card says why -->
            <button
              type="button"
              class="cv-box__btn"
              [disabled]="data.isCustomerSignedIn"
              (click)="onCvSend(customer.customerId, data.isSignedIn, data.isJobseekerSignedIn)"
            >
              {{ 'label.LeaveCv' | translate }}
            </button>
            }
            }
          </div>

          <div class="cv-box__advantages" [innerHTML]="'text.CvBoxDescriptionAdvantages' | translate | safeHtml"></div>
        </div>

        @if (canApply) {
        <span class="cv-box__note">{{ 'label.CanNotLeaveCvWithCompany' | translate }}</span>
        }
      </section>
      }

      <!-- with no announcements the empty state says so; a bare "0" next to the title adds nothing -->
      <div class="jobs__header">
        @if (announcements.length > 0) {
        <h2 class="jobs__title">{{ 'label.CurrentAnnouncements' | translate }}:</h2>
        <span class="jobs__count">{{ announcements.length }}</span>
        } @else {
        <h2 class="jobs__title">{{ 'label.CurrentAnnouncements' | translate }}</h2>
        }
      </div>

      @if (announcements.length > 0) {
      <div class="jobs__list">
        @for (announcement of announcements; track announcement.announcementId) {
        <app-announcement-item
          [userType]="data.userType"
          [providerWebsiteUrlMap]="data.providerWebsiteUrlMap"
          [providerWebsiteId]="providerWebsiteId"
          [announcement]="announcement"
          [pageName]="pageName.CustomerPublicPage"
          [date]="date"
        ></app-announcement-item>
        }
      </div>
      } @else {
      <div class="jobs__empty">
        <p class="jobs__empty-text">{{ 'message.CustomerHasNoActiveAnnouncements' | translate }}</p>
        <a class="jobs__empty-link" routerLink="/search-posting">{{ 'label.ViewAllAnnouncements' | translate }}</a>
      </div>
      }

    </main>
    }
  </div>
  }
  }
  }
</app-spinner>
